// /admin/vendedores — Comerciales externos con comisión por venta.
// Fuente: tabla porteros filtrada por tipo='vendedor' (misma infra que porteros).

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const NAVY = '#1A3461'
const TEAL = '#00A8B5'

const fmtUYU = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-UY')}`
const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-UY', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

export default function AdminVendedores() {
  const [vendedores, setVendedores] = useState([])
  const [loading, setLoading] = useState(true)
  const [busqueda, setBusqueda] = useState('')
  const [modalAlta, setModalAlta] = useState(false)

  async function cargar() {
    setLoading(true)
    const mesActual = new Date().toISOString().slice(0, 7) + '-01'

    const { data: v, error } = await supabase
      .from('porteros')
      .select('id, slug, tipo, email, nombre_completo, comision_default_pct, activo, created_at, banco_cuenta, notas_pago')
      .eq('tipo', 'vendedor')
      .order('created_at', { ascending: false })

    if (error) { console.error(error); setLoading(false); return }

    // Enriquecer con métricas del mes
    const ids = (v || []).map(x => x.id)
    let clientes = {}
    let comisiones = {}
    let ventas = {}          // total atribuido del mes (pagadas + pendientes)
    let ventasPendientes = {} // subset: aún no cobradas
    let cantPendientes = {}

    if (ids.length > 0) {
      const inicioMesISO = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString()
      const [{ data: cli }, { data: com }, { data: ped }] = await Promise.all([
        supabase.from('clientes').select('portero_referido_id, estado').in('portero_referido_id', ids),
        supabase.from('comisiones').select('portero_id, monto_total').eq('mes', mesActual).in('portero_id', ids),
        // Traemos TODOS los pedidos del mes atribuidos al vendedor (por created_at),
        // no solo los pagados. La comisión se devenga con el cobro (viene de la tabla
        // `comisiones`), pero la venta cuenta desde que se creó el pedido — así se
        // ve $1.345 aunque la transferencia esté aún sin confirmar.
        supabase.from('pedidos')
          .select('vendedor_referido_id, monto_subtotal, descuento_cupon, pagado_at, estado, created_at')
          .in('vendedor_referido_id', ids)
          .gte('created_at', inicioMesISO),
      ])
      for (const c of (cli || [])) {
        if (!clientes[c.portero_referido_id]) clientes[c.portero_referido_id] = 0
        if (c.estado === 'activa') clientes[c.portero_referido_id]++
      }
      for (const c of (com || [])) {
        comisiones[c.portero_id] = (comisiones[c.portero_id] || 0) + Number(c.monto_total)
      }
      for (const p of (ped || [])) {
        if (['cancelado', 'rechazado', 'borrador'].includes(p.estado)) continue
        const neto = Number(p.monto_subtotal || 0) - Number(p.descuento_cupon || 0)
        ventas[p.vendedor_referido_id] = (ventas[p.vendedor_referido_id] || 0) + neto
        if (!p.pagado_at) {
          ventasPendientes[p.vendedor_referido_id] = (ventasPendientes[p.vendedor_referido_id] || 0) + neto
          cantPendientes[p.vendedor_referido_id] = (cantPendientes[p.vendedor_referido_id] || 0) + 1
        }
      }
    }

    setVendedores((v || []).map(x => ({
      ...x,
      clientes_activos: clientes[x.id] || 0,
      comision_mes: comisiones[x.id] || 0,
      ventas_mes: ventas[x.id] || 0,
      ventas_pendientes: ventasPendientes[x.id] || 0,
      cant_pendientes: cantPendientes[x.id] || 0,
    })))
    setLoading(false)
  }
  useEffect(() => { cargar() }, [])

  const filtrados = useMemo(() => {
    if (!busqueda.trim()) return vendedores
    const q = busqueda.toLowerCase().trim()
    return vendedores.filter(v =>
      (v.nombre_completo || '').toLowerCase().includes(q) ||
      (v.slug || '').toLowerCase().includes(q) ||
      (v.email || '').toLowerCase().includes(q)
    )
  }, [vendedores, busqueda])

  const stats = useMemo(() => ({
    total: vendedores.length,
    activos: vendedores.filter(v => v.activo).length,
    ventasMes: vendedores.reduce((s, v) => s + v.ventas_mes, 0),
    comisionMes: vendedores.reduce((s, v) => s + v.comision_mes, 0),
  }), [vendedores])

  return (
    <AdminLayout title="Aliados">
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 8px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, margin: '8px 0 16px' }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: NAVY, letterSpacing: -0.4 }}>Aliados</div>
            <div style={{ fontSize: 13, color: '#475569', marginTop: 3 }}>
              {stats.activos} activos · {fmtUYU(stats.ventasMes)} ventas mes · <span style={{ color: TEAL, fontWeight: 600 }}>{fmtUYU(stats.comisionMes)} comisión mes</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="search" placeholder="Buscar por nombre, código, email…"
              value={busqueda} onChange={e => setBusqueda(e.target.value)}
              style={{ padding: '8px 12px', border: '0.5px solid #E2E8F0', borderRadius: 6, fontSize: 13, width: 240, fontFamily: 'inherit' }}
            />
            <button onClick={() => setModalAlta(true)} style={{ background: NAVY, color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              + Alta aliado
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748B' }}>Cargando…</div>
        ) : filtrados.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748B', background: '#fff', borderRadius: 8, border: '0.5px solid #E2E8F0' }}>
            {busqueda ? 'Sin resultados.' : 'No hay aliados todavía. Dale al botón + Alta aliado.'}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {filtrados.map(v => <VendedorCard key={v.id} v={v} />)}
          </div>
        )}
      </div>

      {modalAlta && <ModalAltaVendedor onClose={() => setModalAlta(false)} onDone={() => { setModalAlta(false); cargar() }} />}
    </AdminLayout>
  )
}

function VendedorCard({ v }) {
  return (
    <div style={{ background: '#fff', border: '0.5px solid #E2E8F0', borderRadius: 10, padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: NAVY }}>
              {v.nombre_completo || '(sin nombre)'}
            </span>
            <span style={{
              fontSize: 10.5, padding: '2px 7px', borderRadius: 3, fontWeight: 700,
              background: v.activo ? 'rgba(22,163,74,.1)' : 'rgba(100,116,139,.15)',
              color: v.activo ? '#15803D' : '#475569',
            }}>
              {v.activo ? 'ACTIVO' : 'INACTIVO'}
            </span>
            <span style={{ fontSize: 10.5, padding: '2px 7px', borderRadius: 3, fontWeight: 700, background: 'rgba(0,168,181,.1)', color: '#0E7A82' }}>
              CÓDIGO {v.slug?.toUpperCase()}
            </span>
            <span style={{ fontSize: 10.5, padding: '2px 7px', borderRadius: 3, fontWeight: 700, background: 'rgba(26,52,97,.08)', color: NAVY }}>
              {v.comision_default_pct}% DEFAULT
            </span>
          </div>
          <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
            {v.email || '—'} · Alta {fmtFecha(v.created_at)}
          </div>
          {(v.banco_cuenta || v.notas_pago) && (
            <div style={{ marginTop: 6, padding: '6px 8px', background: '#F8FAFC', borderRadius: 4, fontSize: 11.5, color: '#475569' }}>
              {v.banco_cuenta && <>Cuenta: {v.banco_cuenta}</>}
              {v.banco_cuenta && v.notas_pago && ' · '}
              {v.notas_pago && <>{v.notas_pago}</>}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, textAlign: 'right' }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: NAVY, lineHeight: 1 }}>{v.clientes_activos}</div>
            <div style={{ fontSize: 10, color: '#64748B', fontWeight: 600, letterSpacing: 0.3 }}>CLIENTES</div>
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: NAVY, lineHeight: 1 }}>{fmtUYU(v.ventas_mes)}</div>
            <div style={{ fontSize: 10, color: '#64748B', fontWeight: 600, letterSpacing: 0.3 }}>VENTAS MES</div>
            {v.cant_pendientes > 0 && (
              <div style={{ fontSize: 10, color: '#b45309', fontWeight: 700, marginTop: 3 }} title="Pedidos atribuidos que aún no se cobraron">
                {v.cant_pendientes} sin cobrar · {fmtUYU(v.ventas_pendientes)}
              </div>
            )}
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: TEAL, lineHeight: 1 }}>{fmtUYU(v.comision_mes)}</div>
            <div style={{ fontSize: 10, color: '#64748B', fontWeight: 600, letterSpacing: 0.3 }}>COMISIÓN MES</div>
            <div style={{ fontSize: 9, color: '#64748B', marginTop: 3, fontStyle: 'italic' }}>solo cobradas</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <Link to={`/vendedor?vid=${v.id}`} style={{ ...btnLink, background: NAVY, color: '#fff', borderColor: NAVY }}>
          Ver su panel →
        </Link>
        <a
          href={`https://limpack.uy/?ref=${v.slug?.toUpperCase()}`}
          target="_blank" rel="noreferrer"
          style={btnLink}
        >
          Ver link público ↗
        </a>
      </div>
    </div>
  )
}

function ModalAltaVendedor({ onClose, onDone }) {
  const [email, setEmail] = useState('')
  const [nombre, setNombre] = useState('')
  const [codigo, setCodigo] = useState('')
  const [pct, setPct] = useState(10)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)

  async function submit(e) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const { data, error: e2 } = await supabase.rpc('alta_vendedor', {
        p_email: email.trim(),
        p_nombre_completo: nombre.trim(),
        p_codigo_referido: codigo.trim(),
        p_comision_default_pct: Number(pct),
      })
      if (e2) throw e2
      setResult(data?.[0])
    } catch (e) {
      setError(e.message || 'Error dando de alta')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, color: NAVY, fontSize: 18 }}>Alta aliado</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: '#64748B' }}>×</button>
        </div>

        {result ? (
          <BienvenidaAliado
            email={result.email}
            password={result.password_generada}
            loginUrl={result.login_url}
            nombre={nombre.trim()}
            slug={codigo.trim().toLowerCase()}
            onDone={onDone}
          />
        ) : (
          <form onSubmit={submit}>
            <div style={{ marginBottom: 12 }}>
              <label style={lblStyle}>Nombre completo</label>
              <input required value={nombre} onChange={e => setNombre(e.target.value)} style={inpStyle} placeholder="Juan Pérez" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={lblStyle}>Email (será su login)</label>
              <input required type="email" value={email} onChange={e => setEmail(e.target.value)} style={inpStyle} placeholder="juan@ejemplo.com" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={lblStyle}>Código de referido</label>
              <input required value={codigo} onChange={e => setCodigo(e.target.value.replace(/[^A-Za-z0-9-]/g, '').toUpperCase())} style={inpStyle} placeholder="JUAN" minLength={3} />
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>Solo letras, números y guiones. Se guarda en minúsculas y se muestra en MAYÚSCULAS.</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={lblStyle}>% comisión default</label>
              <input required type="number" value={pct} onChange={e => setPct(e.target.value)} style={inpStyle} min={0} max={100} step={0.5} />
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>Fallback cuando el producto no tiene <strong>Comisión vendedor</strong> cargada.</div>
            </div>

            {error && (
              <div style={{ padding: 10, background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 12, fontSize: 13, color: '#991b1b' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} style={{ background: 'transparent', color: NAVY, border: '1px solid #E2E8F0', padding: '10px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancelar</button>
              <button type="submit" disabled={loading} style={{ background: NAVY, color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: loading ? 'wait' : 'pointer' }}>
                {loading ? 'Creando…' : 'Crear vendedor'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

const lblStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.03, marginBottom: 6 }
const inpStyle = { width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }

const btnLink = {
  fontSize: 12, padding: '5px 10px',
  border: '0.5px solid #CBD5E1', borderRadius: 6,
  color: NAVY, textDecoration: 'none', fontWeight: 600,
  background: '#fff', cursor: 'pointer',
}

// ═══════════════════════════════════════════════════════════════════════════
// BienvenidaAliado — se muestra después del alta con todo listo para copiar:
// manual personalizado, link de referido, y mensaje WhatsApp pre-armado.
// ═══════════════════════════════════════════════════════════════════════════
function BienvenidaAliado({ email, password, loginUrl, nombre, slug, onDone }) {
  const [copiado, setCopiado] = useState(null)
  const nombreCorto = (nombre || '').split(' ')[0] || 'aliado'
  const slugUpper = (slug || '').toUpperCase()

  const manualUrl = 'https://tienda.limpack.uy/manual-aliado-muxf2qov1y.html'
    + `?a=${encodeURIComponent(slug)}`
    + `&n=${encodeURIComponent(nombreCorto)}`
    + `&nf=${encodeURIComponent(nombre)}`
    + `&e=${encodeURIComponent(email)}`
  const refUrl = `https://limpack.uy/?ref=${slugUpper}`

  const msg = [
    `¡Hola ${nombreCorto}! Bienvenida/o a Limpack.`,
    '',
    'Ya tenés tu cuenta de aliada/o creada. Adentro vas a poder ver',
    'tus ventas, comisiones, tu link con tu código y descargar el QR.',
    '',
    '📖 Tu manual (leelo tranqui antes de arrancar, tiene todo el',
    'paso a paso del proceso de compra y cómo funciona el cupón):',
    manualUrl,
    '',
    '🔑 Para entrar la primera vez:',
    `  • URL: ${loginUrl}`,
    `  • Usuario: ${email}`,
    `  • Contraseña temporal: ${password}`,
    '',
    '(Cambiala en tu primera entrada por una que recuerdes).',
    '',
    `Tu código de referido es *${slugUpper}* y tu link para compartir:`,
    refUrl,
    '',
    'Cuando alguien compra con tu link o pone tu código en el carrito,',
    'te queda atribuida la venta. Cobrás comisión sobre el total (sin IVA).',
    'Se liquida a mes vencido.',
    '',
    'Cualquier cosa que no entiendas del manual me pegás un grito.',
    'Éxitos!',
  ].join('\n')

  function copiar(texto, key) {
    navigator.clipboard?.writeText(texto)
    setCopiado(key)
    setTimeout(() => setCopiado(null), 2000)
  }

  const waUrl = `https://wa.me/?text=${encodeURIComponent(msg)}`

  const Row = ({ label, value, k, isBlock }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.03, marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
        <div style={{
          flex: 1, background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: 6,
          padding: '8px 10px', fontSize: 12, fontFamily: 'SFMono-Regular,Menlo,monospace',
          color: NAVY, wordBreak: 'break-all',
          whiteSpace: isBlock ? 'pre-wrap' : 'normal',
          maxHeight: isBlock ? 140 : 'none', overflowY: isBlock ? 'auto' : 'visible',
        }}>{value}</div>
        <button
          onClick={() => copiar(value, k)}
          style={{
            background: copiado === k ? '#065f46' : NAVY, color: '#fff',
            border: 'none', padding: '0 12px', borderRadius: 6,
            fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          {copiado === k ? '✓ Copiado' : 'Copiar'}
        </button>
      </div>
    </div>
  )

  return (
    <div>
      <div style={{ padding: 12, background: '#d1fae5', border: '1px solid #a7f3d0', borderRadius: 8, marginBottom: 14 }}>
        <div style={{ fontWeight: 700, color: '#065f46' }}>Aliado creado ✓</div>
      </div>

      <Row label="Link del manual (personalizado)" value={manualUrl} k="manual" />
      <Row label="Link de referido" value={refUrl} k="ref" />
      <Row label="Password temporal" value={password} k="pass" />

      <div style={{
        margin: '14px 0 12px', padding: 10, background: '#FFF8E1',
        border: '1px solid #FDE68A', borderRadius: 6, fontSize: 12, color: '#78350F',
      }}>
        <strong>Mensaje de bienvenida listo</strong> — copialo o abrí WhatsApp directo con el texto cargado.
      </div>

      <Row label="Mensaje WhatsApp" value={msg} k="msg" isBlock />

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <a
          href={waUrl}
          target="_blank" rel="noreferrer"
          style={{
            flex: 1, background: '#25D366', color: '#fff', textDecoration: 'none',
            padding: '10px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600,
            textAlign: 'center', cursor: 'pointer',
          }}
        >
          📱 Abrir WhatsApp con el mensaje
        </a>
        <button onClick={onDone} style={{
          background: '#fff', color: NAVY, border: '1px solid #E2E8F0',
          padding: '10px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>Cerrar</button>
      </div>
    </div>
  )
}
